import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { GetUserDto } from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { DueDates } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/dueDate.interface';
import { DocumentLink, FcpTreeItemType } from '../../domain/models/fcp.model';
import { financialClosingProcessItemDocument } from '../../domain/reducers/fcp.actions';
import { FcpFeatureState, selectActiveTestItem } from '../../domain/reducers/fcp.reducer';

@UntilDestroy()
@Component({
    selector: 'app-fcp-documents',
    templateUrl: 'fcpDocuments.component.html',
    styleUrls: ['fcpDocuments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FcpDocumentsComponent implements OnInit {
    FcpTreeItemType = FcpTreeItemType;
    docs$: Observable<DocumentLink[]>;
    @Input() lastYear: boolean = false;
    @Input() dueDates: DueDates;
    acceptedExtensions = '.xls,.xlsx,.XLSX,.XLS,.pdf,.PDF';
    @Input() currentUser: GetUserDto;
    fileExtensionError: boolean;

    documentsInProgressUpload: string[] = [];

    constructor(private readonly store: Store<FcpFeatureState>, private readonly actionListener$: ActionsSubject) {}

    ngOnInit() {
        this.docs$ = this.store.select(selectActiveTestItem).pipe(
            map((item) => {
                const relevantDueDate = this.lastYear ? this.dueDates.dueDatePY : this.dueDates.dueDate;
                return (item?.documents || []).filter(
                    (documents) => new Date(documents.dueDate).getTime() === new Date(relevantDueDate).getTime(),
                );
            }),
        );

        this.actionListener$
            .pipe(ofType(financialClosingProcessItemDocument.success), untilDestroyed(this))
            .subscribe(({ payload }) => {
                this.documentsInProgressUpload = this.documentsInProgressUpload.filter((doc) => doc !== payload.name);
            });
    }

    onFileUploadedChanged(files: FileList) {
        const fileExtension = files[0].name.split('.').pop();
        const acceptedExtensions = this.acceptedExtensions.split(',');
        if (!acceptedExtensions.includes('.' + fileExtension)) {
            this.fileExtensionError = true;
            return;
        }

        const formData: FormData = new FormData();
        formData.append('file', files[0]);

        this.documentsInProgressUpload.push(files[0].name);
        const fileMetaData: Pick<DocumentLink, 'name' | 'uploadedByName' | 'dueDate'> = {
            name: files[0].name,
            uploadedByName: `${this.currentUser.firstName} ${this.currentUser.lastName}`,
            dueDate: this.dueDates.dueDate,
        };
        this.store.dispatch(
            financialClosingProcessItemDocument.do({
                file: formData,
                fileMetaData,
            }),
        );
    }

    downloadDocument(document: DocumentLink) {
        this.store.dispatch(
            financialClosingProcessItemDocument.download({ documentId: document.id, documentName: document.name }),
        );
    }

    onDocumentDeleted(document: DocumentLink) {
        this.store.dispatch(financialClosingProcessItemDocument.markfordeletionfcpdocuments({ payload: document.id }));
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();

        const files = event.dataTransfer?.files;

        this.onFileUploadedChanged(files);
    }

    resetFiles($event) {
        $event.target.value = '';
    }
}
